import React, { useState, useEffect, useCallback } from "react";
import {
  Play, Pause, ChevronLeft, ChevronRight, X,
  Clock, Layers, Scale, GitBranch, Search, Radio
} from "lucide-react";

const SLIDES = [
  {
    id: "dashboard",
    title: "Forensic Command Center",
    subtitle: "Delay, claims & risk — one surface",
    desc: "A purpose-built dashboard for forensic planners: total delay days, active claims pipeline, portfolio risk exposure and live programme health. Every KPI drills down into an evidence-grade view.",
    image: "/demo/demo_dashboard.jpg",
    icon: Clock,
    accent: "#E11D48",
    highlights: ["Total delay-days tracker", "Active claims pipeline", "Portfolio risk heat-map", "One-click forensic drilldowns"]
  },
  {
    id: "delay-analysis",
    title: "Delay Analysis",
    subtitle: "CPM, Window, Impacted As-Planned",
    desc: "Open a forensic delay project, log events against the programme, link contractual evidence (notices, correspondences, change orders), classify by cause code and produce narrative reports ready for claim submission.",
    image: "/demo/demo_delay.jpg",
    icon: Clock,
    accent: "#EAB308",
    highlights: ["Cause-code classification", "Evidence linking (NoD, CE, VO)", "Entitlement & concurrency analysis", "Claims-ready narratives"]
  },
  {
    id: "programme",
    title: "Programme Baseline Compare",
    subtitle: "P6 & MSP — baseline vs baseline",
    desc: "Import .xml/.xer schedules from Primavera P6 or MS Project, label each revision (Baseline 0, Rev 1, As-Built) and diff two programmes. Get activity-level drift, critical-path shifts, and an AI forensic narrative on likely causes and claims exposure.",
    image: "/demo/demo_kpi.jpg",
    icon: Layers,
    accent: "#E11D48",
    highlights: ["P6 .xml / .xer import", "Baseline labelling", "Activity-level diff", "AI narrative on drift & causes"]
  },
  {
    id: "claims",
    title: "Claims & Change Register",
    subtitle: "EoT, variations & notices",
    desc: "Track every claim from notice to outcome — submitted, under review, approved, rejected. Tie each entry to contract clauses, delay events and correspondence evidence. Pipeline analytics keep your exposure visible at a glance.",
    image: "/demo/demo_generator.jpg",
    icon: Scale,
    accent: "#EAB308",
    highlights: ["Notice trigger detection", "EoT & cost quantum", "Pipeline status dashboard", "Contractual evidence links"]
  },
  {
    id: "analyzer",
    title: "Contract Analyzer",
    subtitle: "Upload • extract • risk-score",
    desc: "Upload any contract (PDF/DOCX) and the AI engine pulls out clauses, risk flags, entitlement hooks and notice triggers — building a searchable library of the contractual battleground for your claim.",
    image: "/demo/demo_analyzer.jpg",
    icon: Search,
    accent: "#E11D48",
    highlights: ["AI clause extraction", "Risk scoring", "Notice & trigger detection", "Framework recognition"]
  },
  {
    id: "collab",
    title: "Live Evidence Room",
    subtitle: "Real-time collaboration & AI copilot",
    desc: "Work shoulder-to-shoulder with your delay team — live comments on programmes, an AI copilot that drafts claim paragraphs from your evidence, and an organization-wide audit trail of every forensic decision.",
    image: "/demo/demo_clause.jpg",
    icon: Radio,
    accent: "#EAB308",
    highlights: ["Multi-user live comments", "AI claim-draft copilot", "Organization audit log", "Role-based evidence access"]
  }
];

const FeatureShowcase = ({ onClose }) => {
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const SLIDE_DURATION = 6000;

  const next = useCallback(() => {
    setCurrent(p => (p + 1) % SLIDES.length);
    setProgress(0);
  }, []);

  const prev = useCallback(() => {
    setCurrent(p => (p - 1 + SLIDES.length) % SLIDES.length);
    setProgress(0);
  }, []);

  // Auto-advance
  useEffect(() => {
    if (!playing) return;
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) { next(); return 0; }
        return p + (100 / (SLIDE_DURATION / 50));
      });
    }, 50);
    return () => clearInterval(interval);
  }, [playing, next]);

  const slide = SLIDES[current];

  return (
    <div className="fixed inset-0 z-50 bg-[#050508]/95 backdrop-blur-md flex items-center justify-center p-4" data-testid="feature-showcase">
      
      {/* Close */}
      <button onClick={onClose} className="absolute top-5 right-5 z-50 w-10 h-10 flex items-center justify-center border border-zinc-700 bg-zinc-900/80 text-zinc-400 hover:text-white hover:border-zinc-500 transition-all" data-testid="showcase-close">
        <X size={18} />
      </button>

      <div className="w-full max-w-6xl mx-auto">
        
        {/* Top bar with slide indicators */}
        <div className="flex items-center gap-2 mb-5">
          {SLIDES.map((s, i) => (
            <button key={i} onClick={() => { setCurrent(i); setProgress(0); }}
              className="flex-1 h-1 relative overflow-hidden bg-zinc-800/60 cursor-pointer group">
              <div className={`absolute inset-0 transition-all duration-300 ${i < current ? 'bg-zinc-500' : i === current ? '' : 'bg-transparent'}`} />
              {i === current && (
                <div className="absolute inset-y-0 left-0 bg-[#E11D48]" style={{ width: `${progress}%`, transition: 'width 50ms linear' }} />
              )}
              <div className="absolute inset-0 bg-zinc-600 opacity-0 group-hover:opacity-30 transition-opacity" />
            </button>
          ))}
        </div>

        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
          
          {/* Screenshot (3 cols) */}
          <div className="lg:col-span-3 relative">
            <div className="border border-zinc-800 bg-zinc-900/40 overflow-hidden relative">
              <div className="absolute top-0 left-0 right-0 h-8 bg-zinc-900/90 flex items-center px-3 z-10 border-b border-zinc-800">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                </div>
                <span className="text-[9px] text-zinc-600 font-mono ml-3 uppercase tracking-wider">{slide.title}</span>
              </div>
              <img 
                src={slide.image} 
                alt={slide.title}
                className="w-full h-auto mt-0 transition-opacity duration-500"
                style={{ aspectRatio: '1920/800' }}
              />
              {/* Gradient overlay */}
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#050508] to-transparent" />
            </div>
            
            {/* Navigation */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <button onClick={prev} className="w-9 h-9 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white hover:border-zinc-600 transition-all">
                  <ChevronLeft size={16} />
                </button>
                <button onClick={() => setPlaying(!playing)} className="w-9 h-9 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white hover:border-zinc-600 transition-all" data-testid="showcase-play-pause">
                  {playing ? <Pause size={14} /> : <Play size={14} />}
                </button>
                <button onClick={next} className="w-9 h-9 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white hover:border-zinc-600 transition-all">
                  <ChevronRight size={16} />
                </button>
              </div>
              <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider">
                {current + 1} / {SLIDES.length}
              </span>
            </div>
          </div>

          {/* Info panel (2 cols) */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-px" style={{ backgroundColor: slide.accent }} />
              <span className="text-[9px] uppercase tracking-[0.4em] font-mono font-bold" style={{ color: slide.accent }}>
                Feature Tour
              </span>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 border flex items-center justify-center" style={{ borderColor: `${slide.accent}40`, backgroundColor: `${slide.accent}10` }}>
                <slide.icon size={20} style={{ color: slide.accent }} />
              </div>
              <div>
                <h3 className="text-xl font-black text-white font-['Chivo'] uppercase tracking-tight leading-tight">{slide.title}</h3>
                <p className="text-xs text-zinc-500 font-mono">{slide.subtitle}</p>
              </div>
            </div>

            <p className="text-sm text-zinc-400 leading-relaxed mb-6">{slide.desc}</p>

            {/* Highlights */}
            <div className="space-y-2 mb-6">
              {slide.highlights.map((h, i) => (
                <div key={i} className="flex items-center gap-2.5 group">
                  <div className="w-1.5 h-1.5 flex-shrink-0" style={{ backgroundColor: slide.accent }} />
                  <span className="text-xs text-zinc-500 group-hover:text-zinc-300 transition-colors">{h}</span>
                </div>
              ))}
            </div>

            {/* Mini nav - all features */}
            <div className="border-t border-zinc-800/60 pt-4">
              <p className="text-[8px] uppercase tracking-[0.3em] text-zinc-700 font-mono mb-3">All Features</p>
              <div className="grid grid-cols-2 gap-1.5">
                {SLIDES.map((s, i) => (
                  <button key={i} onClick={() => { setCurrent(i); setProgress(0); }}
                    className={`text-left px-2.5 py-2 text-[10px] font-mono uppercase tracking-wider transition-all border ${
                      i === current 
                        ? 'text-white border-zinc-700 bg-zinc-800/40' 
                        : 'text-zinc-600 border-transparent hover:text-zinc-400 hover:border-zinc-800'
                    }`}>
                    <div className="flex items-center gap-1.5">
                      <s.icon size={10} />
                      <span className="truncate">{s.title.replace("AI ", "")}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureShowcase;
